'use client';
import Link from 'next/link';
import Image from 'next/image';
import { FunctionComponent, Suspense, useState } from 'react';
import { PrimaryCtaLink } from './PrimaryCtaLink';
import { UserDropdown } from './UserDropdown';
import { TwitterXIcon } from '../../../../components/Icons/TwitterXIcon';
import { FacebookIcon } from '../../../../components/Icons/FacebookIcon';
import { LinkedinIcon } from '../../../../components/Icons/LinkedinIcon';
import { YoutubeIcon } from '../../../../components/Icons/YoutubeIcon';
import { EnvelopeOpenIcon } from '../../../../components/Icons/EnvelopeOpenIcon';
import { AngleDownRegularIcon } from '../../../../components/Icons/AngleDownRegularIcon';
import styles from './styles.module.scss';
import classNames from 'classnames';

export type MarketplaceShellHeaderProps = {
    headerMaxWidth?: 'wide' | 'small' | 'none';
};

export const MarketplaceShellHeader: FunctionComponent<MarketplaceShellHeaderProps> = ({
    headerMaxWidth,
}) => {
    const [menuHidden, setMenuHidden] = useState(false);

    const closeMenu = () => {
        const mobileMenuCheckbox = document.getElementById(
            'mobile-menu-toggle'
        ) as HTMLInputElement;
        if (mobileMenuCheckbox) {
            mobileMenuCheckbox.checked = false;
        }

        setMenuHidden(true);
    };

    const resetMenuHidden = () => {
        setMenuHidden(false);
    };

    return (
        <header className={styles.header}>
            <div
                className={classNames(styles.header_container, {
                    [styles.header_container_wide]: headerMaxWidth === 'wide',
                    [styles.header_container_small]: headerMaxWidth === 'small',
                    [styles.header_container_none]: headerMaxWidth === 'none',
                })}
            >
                <div className={styles.cademy_header_logo}>
                    <Link href="/">
                        <Image
                            src={`${process.env.NEXT_PUBLIC_ASSETS_URL}/logos/logo-resized.svg`}
                            alt="Cademy logo"
                            width={160}
                            height={26}
                        />
                        <span className="screen_reader_hidden">Cademy Marketplace</span>
                    </Link>
                </div>

                <nav className={styles.desktop_nav}>
                    <div
                        className={classNames(styles.nav_menu, {
                            [styles.menu_hidden]: menuHidden,
                        })}
                        onMouseLeave={resetMenuHidden}
                    >
                        <div className={styles.nav_item_with_dropdown}>
                            <button className={styles.nav_link}>
                                Product
                                <AngleDownRegularIcon width="16" />
                            </button>
                            <div className={styles.mega_menu}>
                                <div className={styles.mega_menu_content}>
                                    <div className={styles.mega_menu_section}>
                                        <h3 className={styles.section_heading}>
                                            Platform Overview
                                        </h3>
                                        <ul className={styles.menu_list}>
                                            <li>
                                                <Link
                                                    href="/training-management-system"
                                                    onClick={closeMenu}
                                                >
                                                    Training Management System
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/learning-management-system"
                                                    onClick={closeMenu}
                                                >
                                                    Learning Management System
                                                </Link>
                                            </li>
                                            <li>
                                                <Link href="/training-crm" onClick={closeMenu}>
                                                    Training CRM
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/course-booking-system"
                                                    onClick={closeMenu}
                                                >
                                                    Course Booking System
                                                </Link>
                                            </li>
                                            <li>
                                                <Link href="/ai-course-builder" onClick={closeMenu}>
                                                    AI Course Builder
                                                </Link>
                                            </li>
                                        </ul>
                                    </div>
                                    <div className={styles.mega_menu_section}>
                                        <h3 className={styles.section_heading}>
                                            Partners & Integrations
                                        </h3>
                                        <ul className={styles.menu_list}>
                                            <li>
                                                <Link href="/apps#scheduling" onClick={closeMenu}>
                                                    Google Calendar
                                                </Link>
                                            </li>
                                            <li>
                                                <Link href="/apps#scheduling" onClick={closeMenu}>
                                                    Outlook Calendar
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/apps#accounting-and-invoicing"
                                                    onClick={closeMenu}
                                                >
                                                    Xero
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/apps#sales-and-marketing"
                                                    onClick={closeMenu}
                                                >
                                                    Zapier
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/apps#course-creation-and-delivery"
                                                    onClick={closeMenu}
                                                >
                                                    Zoom
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/apps#payments-and-invoicing"
                                                    onClick={closeMenu}
                                                >
                                                    Stripe
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/apps#payments-and-invoicing"
                                                    onClick={closeMenu}
                                                >
                                                    Paypal
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/apps#payments-and-invoicing"
                                                    onClick={closeMenu}
                                                >
                                                    Klarna
                                                </Link>
                                            </li>
                                        </ul>
                                    </div>
                                    <div className={styles.mega_menu_section}>
                                        <h3 className={styles.section_heading}>Switch from</h3>
                                        <ul className={styles.menu_list}>
                                            <li>
                                                <Link
                                                    href="/switch/best-arlo-alternative"
                                                    onClick={closeMenu}
                                                >
                                                    Cademy VS Arlo
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/switch/best-bookwhen-alternative"
                                                    onClick={closeMenu}
                                                >
                                                    Cademy VS Bookwhen
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/switch/best-eventbrite-alternative"
                                                    onClick={closeMenu}
                                                >
                                                    Cademy VS Eventbrite
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/switch/best-kajabi-alternative"
                                                    onClick={closeMenu}
                                                >
                                                    Cademy VS Kajabi
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/switch/best-learnworlds-alternative"
                                                    onClick={closeMenu}
                                                >
                                                    Cademy VS LearnWorlds
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/switch/best-learndash-alternative"
                                                    onClick={closeMenu}
                                                >
                                                    Cademy VS LearnDash
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/switch/best-moodle-alternative"
                                                    onClick={closeMenu}
                                                >
                                                    Cademy VS Moodle
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/switch/best-talentlms-alternative"
                                                    onClick={closeMenu}
                                                >
                                                    Cademy VS TalentLMS
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/switch/best-teachable-alternative"
                                                    onClick={closeMenu}
                                                >
                                                    Cademy VS Teachable
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/switch/best-thinkific-alternative"
                                                    onClick={closeMenu}
                                                >
                                                    Cademy VS Thinkific
                                                </Link>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.nav_item_with_dropdown}>
                            <button className={styles.nav_link}>
                                Features
                                <AngleDownRegularIcon width="16" />
                            </button>
                            <div className={styles.mega_menu}>
                                <div className={styles.mega_menu_content}>
                                    <div className={styles.mega_menu_section}>
                                        <h3 className={styles.section_heading}>
                                            Course Delivery & Engagement
                                        </h3>
                                        <ul className={styles.menu_list}>
                                            <li>
                                                <Link
                                                    href="/features#in-person-online-on-demand-and-blended-courses"
                                                    onClick={closeMenu}
                                                >
                                                    In-Person, Online, On-Demand & Blended Courses
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/features#learning-pathways"
                                                    onClick={closeMenu}
                                                >
                                                    Learning Pathways
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/features#drip-feeds-and-deadlines"
                                                    onClick={closeMenu}
                                                >
                                                    Drip Feeds & Deadlines
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/features#quizzes-and-assessments"
                                                    onClick={closeMenu}
                                                >
                                                    Quizzes & Assessments
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/features#certificates"
                                                    onClick={closeMenu}
                                                >
                                                    Certificates
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/features#forums-and-communities"
                                                    onClick={closeMenu}
                                                >
                                                    Forums & Communities
                                                </Link>
                                            </li>
                                        </ul>
                                    </div>
                                    <div className={styles.mega_menu_section}>
                                        <h3 className={styles.section_heading}>
                                            Operations & Admin
                                        </h3>
                                        <ul className={styles.menu_list}>
                                            <li>
                                                <Link
                                                    href="/features#booking-and-scheduling"
                                                    onClick={closeMenu}
                                                >
                                                    Booking & Scheduling
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/features#payments-and-invoicing"
                                                    onClick={closeMenu}
                                                >
                                                    Payments & Invoicing
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/features#facilitator-management"
                                                    onClick={closeMenu}
                                                >
                                                    Facilitator Management
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/features#automations-and-workflows"
                                                    onClick={closeMenu}
                                                >
                                                    Automations & Workflows
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/features#reporting-and-analytics"
                                                    onClick={closeMenu}
                                                >
                                                    Reporting & Analytics
                                                </Link>
                                            </li>
                                        </ul>
                                    </div>
                                    <div className={styles.mega_menu_section}>
                                        <h3 className={styles.section_heading}>Business Tools</h3>
                                        <ul className={styles.menu_list}>
                                            <li>
                                                <Link
                                                    href="/features#sales-and-marketing"
                                                    onClick={closeMenu}
                                                >
                                                    Sales & Marketing
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/features#reporting-and-analytics"
                                                    onClick={closeMenu}
                                                >
                                                    Reporting & Analytics
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/features#b2b-portals-and-organisations"
                                                    onClick={closeMenu}
                                                >
                                                    B2B Portals & Organisations
                                                </Link>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.nav_item_with_dropdown}>
                            <button className={styles.nav_link}>
                                Solutions
                                <AngleDownRegularIcon width="16" />
                            </button>
                            <div className={styles.mega_menu}>
                                <div className={styles.mega_menu_content}>
                                    <div className={styles.mega_menu_section}>
                                        <h3 className={styles.section_heading}>
                                            By Educator Profile
                                        </h3>
                                        <ul className={styles.menu_list}>
                                            <li>
                                                <Link
                                                    href="/solutions/for-training-providers"
                                                    onClick={closeMenu}
                                                >
                                                    Training Providers
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/solutions/for-education-institutions"
                                                    onClick={closeMenu}
                                                >
                                                    Education Institutions
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/solutions/for-regulated-and-accredited-training"
                                                    onClick={closeMenu}
                                                >
                                                    Regulated & Accredited Training
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/solutions/for-certification-and-awarding-bodies"
                                                    onClick={closeMenu}
                                                >
                                                    Certification & Awarding Bodies
                                                </Link>
                                            </li>
                                        </ul>
                                    </div>
                                    <div className={styles.mega_menu_section}>
                                        <h3 className={styles.section_heading}>By Role</h3>
                                        <ul className={styles.menu_list}>
                                            <li>
                                                <Link href="/solutions/for-hr" onClick={closeMenu}>
                                                    HR teams
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/solutions/for-corporate-learning-and-development"
                                                    onClick={closeMenu}
                                                >
                                                    L&D teams
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/solutions/for-compliance"
                                                    onClick={closeMenu}
                                                >
                                                    Compliance teams
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/solutions/for-sales-and-product-teams"
                                                    onClick={closeMenu}
                                                >
                                                    Sales & product teams
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/solutions/for-customer-success"
                                                    onClick={closeMenu}
                                                >
                                                    Customer Success teams
                                                </Link>
                                            </li>
                                        </ul>
                                    </div>
                                    <div className={styles.mega_menu_section}>
                                        <h3 className={styles.section_heading}>
                                            By Training Model
                                        </h3>
                                        <ul className={styles.menu_list}>
                                            <li>
                                                <Link href="/solutions/for-b2c" onClick={closeMenu}>
                                                    B2C
                                                </Link>
                                            </li>
                                            <li>
                                                <Link href="/solutions/for-b2b" onClick={closeMenu}>
                                                    B2B
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="/solutions/for-corporate-learning-and-development"
                                                    onClick={closeMenu}
                                                >
                                                    Corporate L&D
                                                </Link>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.nav_item_with_dropdown}>
                            <button className={styles.nav_link}>
                                Resources
                                <AngleDownRegularIcon width="16" />
                            </button>
                            <div className={styles.mega_menu}>
                                <div className={styles.mega_menu_content}>
                                    <div className={styles.mega_menu_section}>
                                        <h3 className={styles.section_heading}>Learn More</h3>
                                        <ul className={styles.menu_list}>
                                            <li>
                                                <Link href="/blog" onClick={closeMenu}>
                                                    Blog
                                                </Link>
                                            </li>
                                            <li>
                                                <Link href="/blog" onClick={closeMenu}>
                                                    Product Updates
                                                </Link>
                                            </li>
                                            <li>
                                                <Link
                                                    href="https://discover.cademy.io/"
                                                    onClick={closeMenu}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Webinars
                                                </Link>
                                            </li>
                                        </ul>
                                    </div>
                                    <div className={styles.mega_menu_section}>
                                        <h3 className={styles.section_heading}>Support</h3>
                                        <ul className={styles.menu_list}>
                                            <li>
                                                <Link
                                                    href="https://help.cademy.io/"
                                                    onClick={closeMenu}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Help Centre
                                                </Link>
                                            </li>
                                            <li>
                                                <Link href="/terms" onClick={closeMenu}>
                                                    Terms
                                                </Link>
                                            </li>
                                            <li>
                                                <Link href="/privacy" onClick={closeMenu}>
                                                    Privacy
                                                </Link>
                                            </li>
                                        </ul>
                                    </div>
                                    <div className={styles.mega_menu_section}>
                                        <h3 className={styles.section_heading}>Company</h3>
                                        <ul className={styles.menu_list}>
                                            <li>
                                                <Link href="/about" onClick={closeMenu}>
                                                    About Us
                                                </Link>
                                            </li>
                                            <li>
                                                <Link href="/careers" onClick={closeMenu}>
                                                    Careers
                                                </Link>
                                            </li>
                                            <li>
                                                <Link href="/contact" onClick={closeMenu}>
                                                    Contact Us
                                                </Link>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.nav_item}>
                            <Link href="/pricing" className={styles.nav_link}>
                                Pricing
                            </Link>
                        </div>
                    </div>

                    <div className={styles.nav_actions}>
                        {/* https://nextjs.org/docs/messages/missing-suspense-with-csr-bailout */}
                        <Suspense>
                            <UserDropdown />
                        </Suspense>
                        <Suspense>
                            <PrimaryCtaLink className={styles.callToAction} />
                        </Suspense>
                    </div>
                </nav>

                <nav className={styles.mobile_nav_wrapper}>
                    <input
                        id="mobile-menu-toggle"
                        className={styles.menuCheckbox}
                        type="checkbox"
                    />
                    <label
                        className={styles.hamburgerButton}
                        htmlFor="mobile-menu-toggle"
                        role="button"
                        aria-haspopup="true"
                        aria-controls="mobile-nav"
                        aria-label="Menu toggle"
                    >
                        <span className={styles.hamburgerLine}></span>
                    </label>
                    <div className={styles.mobile_nav} id="mobile-nav">
                        <div className={styles.mobile_nav_content}>
                            <div className={styles.mobile_menu_section}>
                                <Link
                                    href="/"
                                    className={styles.mobile_menu_link}
                                    onClick={closeMenu}
                                >
                                    Home
                                </Link>
                            </div>

                            <div className={styles.mobile_menu_section}>
                                <input
                                    id="mobile-product-toggle"
                                    className={styles.mobile_submenu_checkbox}
                                    type="checkbox"
                                />
                                <label
                                    htmlFor="mobile-product-toggle"
                                    className={styles.mobile_menu_toggle}
                                >
                                    Product
                                    <AngleDownRegularIcon width="16" />
                                </label>
                                <div className={styles.mobile_submenu}>
                                    <div className={styles.mobile_submenu_group}>
                                        <h4 className={styles.mobile_submenu_heading}>
                                            Platform Overview
                                        </h4>
                                        <Link
                                            href="/training-management-system"
                                            onClick={closeMenu}
                                        >
                                            Training Management System
                                        </Link>
                                        <Link
                                            href="/learning-management-system"
                                            onClick={closeMenu}
                                        >
                                            Learning Management System
                                        </Link>
                                        <Link href="/training-crm" onClick={closeMenu}>
                                            Training CRM
                                        </Link>
                                        <Link href="/course-booking-system" onClick={closeMenu}>
                                            Course Booking System
                                        </Link>
                                        <Link href="/ai-course-builder" onClick={closeMenu}>
                                            AI Course Builder
                                        </Link>
                                    </div>
                                    <div className={styles.mobile_submenu_group}>
                                        <h4 className={styles.mobile_submenu_heading}>
                                            Partners & Integrations
                                        </h4>
                                        <Link href="/apps#scheduling" onClick={closeMenu}>
                                            Google Calendar
                                        </Link>
                                        <Link href="/apps#scheduling" onClick={closeMenu}>
                                            Outlook Calendar
                                        </Link>
                                        <Link
                                            href="/apps#accounting-and-invoicing"
                                            onClick={closeMenu}
                                        >
                                            Xero
                                        </Link>
                                        <Link href="/apps#sales-and-marketing" onClick={closeMenu}>
                                            Zapier
                                        </Link>
                                        <Link
                                            href="/apps#course-creation-and-delivery"
                                            onClick={closeMenu}
                                        >
                                            Zoom
                                        </Link>
                                        <Link
                                            href="/apps#payments-and-invoicing"
                                            onClick={closeMenu}
                                        >
                                            Stripe
                                        </Link>
                                        <Link
                                            href="/apps#payments-and-invoicing"
                                            onClick={closeMenu}
                                        >
                                            Paypal
                                        </Link>
                                        <Link
                                            href="/apps#payments-and-invoicing"
                                            onClick={closeMenu}
                                        >
                                            Klarna
                                        </Link>
                                    </div>
                                    <div className={styles.mobile_submenu_group}>
                                        <h4 className={styles.mobile_submenu_heading}>
                                            Switch From
                                        </h4>
                                        <Link
                                            href="/switch/best-arlo-alternative"
                                            onClick={closeMenu}
                                        >
                                            Cademy VS Arlo
                                        </Link>
                                        <Link
                                            href="/switch/best-bookwhen-alternative"
                                            onClick={closeMenu}
                                        >
                                            Cademy VS Bookwhen
                                        </Link>
                                        <Link
                                            href="/switch/best-eventbrite-alternative"
                                            onClick={closeMenu}
                                        >
                                            Cademy VS Eventbrite
                                        </Link>
                                        <Link
                                            href="/switch/best-kajabi-alternative"
                                            onClick={closeMenu}
                                        >
                                            Cademy VS Kajabi
                                        </Link>
                                        <Link
                                            href="/switch/best-learnworlds-alternative"
                                            onClick={closeMenu}
                                        >
                                            Cademy VS LearnWorlds
                                        </Link>
                                        <Link
                                            href="/switch/best-learndash-alternative"
                                            onClick={closeMenu}
                                        >
                                            Cademy VS LearnDash
                                        </Link>
                                        <Link
                                            href="/switch/best-moodle-alternative"
                                            onClick={closeMenu}
                                        >
                                            Cademy VS Moodle
                                        </Link>
                                        <Link
                                            href="/switch/best-talentlms-alternative"
                                            onClick={closeMenu}
                                        >
                                            Cademy VS TalentLMS
                                        </Link>
                                        <Link
                                            href="/switch/best-teachable-alternative"
                                            onClick={closeMenu}
                                        >
                                            Cademy VS Teachable
                                        </Link>
                                        <Link
                                            href="/switch/best-thinkific-alternative"
                                            onClick={closeMenu}
                                        >
                                            Cademy VS Thinkific
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.mobile_menu_section}>
                                <input
                                    id="mobile-features-toggle"
                                    className={styles.mobile_submenu_checkbox}
                                    type="checkbox"
                                />
                                <label
                                    htmlFor="mobile-features-toggle"
                                    className={styles.mobile_menu_toggle}
                                >
                                    Features
                                    <AngleDownRegularIcon width="16" />
                                </label>
                                <div className={styles.mobile_submenu}>
                                    <div className={styles.mobile_submenu_group}>
                                        <h4 className={styles.mobile_submenu_heading}>
                                            Course Delivery & Engagement
                                        </h4>
                                        <Link
                                            href="/features#in-person-online-on-demand-and-blended-courses"
                                            onClick={closeMenu}
                                        >
                                            In-Person, Online, On-Demand & Blended Courses
                                        </Link>
                                        <Link
                                            href="/features#learning-pathways"
                                            onClick={closeMenu}
                                        >
                                            Learning Pathways
                                        </Link>
                                        <Link
                                            href="/features#drip-feeds-and-deadlines"
                                            onClick={closeMenu}
                                        >
                                            Drip Feeds & Deadlines
                                        </Link>
                                        <Link
                                            href="/features#quizzes-and-assessments"
                                            onClick={closeMenu}
                                        >
                                            Quizzes & Assessments
                                        </Link>
                                        <Link href="/features#certificates" onClick={closeMenu}>
                                            Certificates
                                        </Link>
                                        <Link
                                            href="/features#forums-and-communities"
                                            onClick={closeMenu}
                                        >
                                            Forums & Communities
                                        </Link>
                                    </div>
                                    <div className={styles.mobile_submenu_group}>
                                        <h4 className={styles.mobile_submenu_heading}>
                                            Operations & Admin
                                        </h4>
                                        <Link
                                            href="/features#booking-and-scheduling"
                                            onClick={closeMenu}
                                        >
                                            Booking & Scheduling
                                        </Link>
                                        <Link
                                            href="/features#payments-and-invoicing"
                                            onClick={closeMenu}
                                        >
                                            Payments & Invoicing
                                        </Link>
                                        <Link
                                            href="/features#facilitator-management"
                                            onClick={closeMenu}
                                        >
                                            Facilitator Management
                                        </Link>
                                        <Link
                                            href="/features#automations-and-workflows"
                                            onClick={closeMenu}
                                        >
                                            Automations & Workflows
                                        </Link>
                                        <Link
                                            href="/features#reporting-and-analytics"
                                            onClick={closeMenu}
                                        >
                                            Reporting & Analytics
                                        </Link>
                                    </div>
                                    <div className={styles.mobile_submenu_group}>
                                        <h4 className={styles.mobile_submenu_heading}>
                                            Business Tools
                                        </h4>
                                        <Link
                                            href="/features#sales-and-marketing"
                                            onClick={closeMenu}
                                        >
                                            Sales & Marketing
                                        </Link>
                                        <Link
                                            href="/features#reporting-and-analytics"
                                            onClick={closeMenu}
                                        >
                                            Reporting & Analytics
                                        </Link>
                                        <Link
                                            href="/features#b2b-portals-and-organisations"
                                            onClick={closeMenu}
                                        >
                                            B2B Portals & Organisations
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.mobile_menu_section}>
                                <input
                                    id="mobile-solutions-toggle"
                                    className={styles.mobile_submenu_checkbox}
                                    type="checkbox"
                                />
                                <label
                                    htmlFor="mobile-solutions-toggle"
                                    className={styles.mobile_menu_toggle}
                                >
                                    Solutions
                                    <AngleDownRegularIcon width="16" />
                                </label>
                                <div className={styles.mobile_submenu}>
                                    <div className={styles.mobile_submenu_group}>
                                        <h4 className={styles.mobile_submenu_heading}>
                                            By Educator Profile
                                        </h4>
                                        <Link
                                            href="/solutions/for-training-providers"
                                            onClick={closeMenu}
                                        >
                                            Training Providers
                                        </Link>
                                        <Link
                                            href="/solutions/for-education-institutions"
                                            onClick={closeMenu}
                                        >
                                            Education Institutions
                                        </Link>
                                        <Link
                                            href="/solutions/for-regulated-and-accredited-training"
                                            onClick={closeMenu}
                                        >
                                            Regulated & Accredited Training
                                        </Link>
                                        <Link
                                            href="/solutions/for-certification-and-awarding-bodies"
                                            onClick={closeMenu}
                                        >
                                            Certification & Awarding Bodies
                                        </Link>
                                    </div>
                                    <div className={styles.mobile_submenu_group}>
                                        <h4 className={styles.mobile_submenu_heading}>By Role</h4>
                                        <Link href="/solutions/for-hr" onClick={closeMenu}>
                                            HR teams
                                        </Link>
                                        <Link
                                            href="/solutions/for-corporate-learning-and-development"
                                            onClick={closeMenu}
                                        >
                                            L&D teams
                                        </Link>
                                        <Link href="/solutions/for-compliance" onClick={closeMenu}>
                                            Compliance teams
                                        </Link>
                                        <Link
                                            href="/solutions/for-sales-and-product-teams"
                                            onClick={closeMenu}
                                        >
                                            Sales & product teams
                                        </Link>
                                        <Link
                                            href="/solutions/for-customer-success"
                                            onClick={closeMenu}
                                        >
                                            Customer Success teams
                                        </Link>
                                    </div>
                                    <div className={styles.mobile_submenu_group}>
                                        <h4 className={styles.mobile_submenu_heading}>
                                            By Training Model
                                        </h4>
                                        <Link href="/solutions/for-b2c" onClick={closeMenu}>
                                            B2C
                                        </Link>
                                        <Link href="/solutions/for-b2b" onClick={closeMenu}>
                                            B2B
                                        </Link>
                                        <Link
                                            href="/solutions/for-corporate-learning-and-development"
                                            onClick={closeMenu}
                                        >
                                            Corporate L&D
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.mobile_menu_section}>
                                <input
                                    id="mobile-resources-toggle"
                                    className={styles.mobile_submenu_checkbox}
                                    type="checkbox"
                                />
                                <label
                                    htmlFor="mobile-resources-toggle"
                                    className={styles.mobile_menu_toggle}
                                >
                                    Resources
                                    <AngleDownRegularIcon width="16" />
                                </label>
                                <div className={styles.mobile_submenu}>
                                    <div className={styles.mobile_submenu_group}>
                                        <h4 className={styles.mobile_submenu_heading}>
                                            Learn More
                                        </h4>
                                        <Link href="/blog" onClick={closeMenu}>
                                            Blog
                                        </Link>
                                        <Link href="/blog" onClick={closeMenu}>
                                            Product Updates
                                        </Link>
                                        <Link
                                            href="https://discover.cademy.io/"
                                            onClick={closeMenu}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Webinars
                                        </Link>
                                    </div>
                                    <div className={styles.mobile_submenu_group}>
                                        <h4 className={styles.mobile_submenu_heading}>Support</h4>
                                        <Link
                                            href="https://help.cademy.io/"
                                            onClick={closeMenu}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Help Centre
                                        </Link>
                                        <Link href="/terms" onClick={closeMenu}>
                                            Terms
                                        </Link>
                                        <Link href="/privacy" onClick={closeMenu}>
                                            Privacy
                                        </Link>
                                    </div>
                                    <div className={styles.mobile_submenu_group}>
                                        <h4 className={styles.mobile_submenu_heading}>Company</h4>
                                        <Link href="/about" onClick={closeMenu}>
                                            About Us
                                        </Link>
                                        <Link href="/careers" onClick={closeMenu}>
                                            Careers
                                        </Link>
                                        <Link href="/contact" onClick={closeMenu}>
                                            Contact Us
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.mobile_menu_section}>
                                <Link
                                    href="/pricing"
                                    className={styles.mobile_menu_link}
                                    onClick={closeMenu}
                                >
                                    Pricing
                                </Link>
                            </div>

                            <div className={styles.social_icons}>
                                <ul>
                                    <li>
                                        <Link
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href="https://twitter.com/cademy__"
                                            aria-label="Twitter"
                                        >
                                            <TwitterXIcon />
                                        </Link>
                                    </li>
                                    <li>
                                        <Link
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href="https://www.facebook.com/cademy.co.uk"
                                            aria-label="Facebook"
                                        >
                                            <FacebookIcon />
                                        </Link>
                                    </li>
                                    <li>
                                        <Link
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href="https://www.linkedin.com/company/cademy-io"
                                            aria-label="LinkedIn"
                                        >
                                            <LinkedinIcon />
                                        </Link>
                                    </li>
                                    <li>
                                        <Link
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href="https://www.youtube.com/@cademy_"
                                            aria-label="YouTube"
                                        >
                                            <YoutubeIcon />
                                        </Link>
                                    </li>
                                    <li>
                                        <Link
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href="https://upscri.be/hxh121"
                                            aria-label="Subscribe to email updates"
                                        >
                                            <EnvelopeOpenIcon />
                                        </Link>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </nav>
            </div>
        </header>
    );
};
